import { Formik, FormikProps } from 'formik'
import { Box, useToast } from 'native-base'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useLazyQuery, useMutation } from '@apollo/client'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useGateValue } from '@statsig/react-bindings'

import { AppContext } from '../../context/AppContext'
import {
  ConsumerSupportCase,
  ConsumerSupportCaseCategory,
  ConsumerSupportCaseStatus,
  CREATE_CONSUMER_SUPPORT_CASE,
  CreateConsumerSupportCaseInput,
  GET_ORDER_BY_TRACKING_ID_CWA,
  Order,
} from '@consumer/graphql'
import { useScreenSize } from '../../utils/displayUtils'
import { STATSIG_FEATURE_GATES, stripPhoneNumber } from '@consumer/shared'
import { logError } from '../../utils/monitorClient'
import { Loader, OrderInfoHeader } from '@consumer/ui'

import { ToastAlert } from '../Instructions/Common/ToastAlert'
import IssueSubmission from '../IssueSubmission'
import type { IssueData } from '../IssueSubmission'
import ConsentCheckbox from './ConsentCheckbox'
import DeliveryDetailsSection from './DeliveryDetailsSection'
import PageHeader from './PageHeader'
import SubmitButton from './SubmitButton'
import YourDetailsSection from './YourDetailsSection'
import { DEFLECTION_ISSUE_TYPES, IssueFormValues, IssueSubmissionResult, IssueType } from './types'
import { mapCaseCategoryToIssueType, mapIssueTypeToCaseCategory } from './utils'
import { issueFormValidationSchema } from './validation'

// Copy previously hardcoded inside the deleted ./LoadingSpinner component. Kept
// at the call site until this flow goes through i18n (CBP-3944 follow-up:
// CWA report-issue copy is not yet keyed in packages/shared i18n).
const LOADING_ORDER_MESSAGE = 'Loading order information...'
const SUBMITTING_ISSUE_MESSAGE = 'Submitting issue...'

// The deleted LoadingSpinner's outer spacing (py={10}).
const loaderSpacing = { paddingVertical: 40 } as const

// Helper function to check if order is empty or has default values
const isOrderEmpty = (order?: Order): boolean => {
  if (!order) return true
  return (
    !order.orderId || order.orderId === '' || !order.packages || order.packages.length === 0 || order.clientName === ''
  )
}

// Re-export types and constants for external use
export { DEFLECTION_ISSUE_TYPES, IssueSubmissionResult, IssueType, ISSUE_TYPE_LABELS } from './types'

/** Page that lets a consumer report a delivery issue — handles order loading, form state, and submission. */
export default function ReportIssuePage() {
  const screenSize = useScreenSize()
  const isMobileScreen = screenSize.width < 800
  const { trackingId: trackingIdParam } = useParams<{ trackingId?: string }>()
  const { order, setOrder } = useContext(AppContext)
  const existingCaseData: ConsumerSupportCase | undefined =
    order?.packages.find(pkg => pkg.trackingId === trackingIdParam && pkg.consumerSupportCase)?.consumerSupportCase ||
    undefined
  const { show: showToast, close: closeToast } = useToast()
  const [issueData, setIssueData] = useState<IssueData | null>(
    existingCaseData
      ? {
          trackingId: trackingIdParam || '',
          issueType: mapCaseCategoryToIssueType(existingCaseData.caseCategory),
          issueDetail: existingCaseData.caseDetails || '',
          issueSubmissionResult: IssueSubmissionResult.ALREADY_SUBMITTED,
          name: '', // Contact fields not available for preexisting cases
          email: '',
          cellPhone: '',
          consentChecked: false,
          issueId: existingCaseData.caseId,
          taskCreated: false, // tasks only created for specific issues
          imageData: null,
        }
      : null
  )
  // const imageDataRef = useRef<ImageData | null>(null)
  const includeConsumerSupportCases = useGateValue(STATSIG_FEATURE_GATES.cwa_support_escalation)

  const [createConsumerSupportCase, { loading: isSubmittingCase }] = useMutation(CREATE_CONSUMER_SUPPORT_CASE, {
    // Refetch the order query after creating a support case
    refetchQueries: [GET_ORDER_BY_TRACKING_ID_CWA],
  })
  const [getOrderByTrackingIdCWA, { data: orderData, loading: isLoadingOrder }] = useLazyQuery<{ order: Order }>(
    GET_ORDER_BY_TRACKING_ID_CWA,
    {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  )
  const hasCheckedForExistingCase = useRef<string | undefined>(undefined)
  const hasFetchedOrder = useRef(false)

  // Reset check flag and issueData when trackingId changes
  useEffect(() => {
    if (hasCheckedForExistingCase.current !== trackingIdParam) {
      hasCheckedForExistingCase.current = undefined // Reset to allow re-checking for new trackingId
      setIssueData(null) // Reset issueData when trackingId changes
      hasFetchedOrder.current = false // Allow refetching order for new trackingId
    }
  }, [trackingIdParam])

  // Fetch order data if it's empty and we have a trackingId
  useEffect(() => {
    if (trackingIdParam && isOrderEmpty(order) && !hasFetchedOrder.current) {
      hasFetchedOrder.current = true
      getOrderByTrackingIdCWA({
        variables: { trackingId: trackingIdParam, includeConsumerSupportCases },
      })
    }
  }, [trackingIdParam, order, getOrderByTrackingIdCWA, includeConsumerSupportCases])

  // Update order in context when query data is available
  useEffect(() => {
    if (orderData?.order) {
      setOrder(orderData.order)
    }
  }, [orderData, setOrder])

  // Check for existing consumerSupportCase when order data is available
  useEffect(() => {
    // Use orderData first (most up-to-date), fallback to order from context
    const currentOrder = orderData?.order || order

    if (
      currentOrder?.packages &&
      trackingIdParam &&
      hasCheckedForExistingCase.current !== trackingIdParam &&
      !isOrderEmpty(currentOrder)
    ) {
      // Mark that we've checked for this trackingId
      hasCheckedForExistingCase.current = trackingIdParam

      // Find the package with the matching trackingId
      const packageWithCase = currentOrder.packages.find(
        pkg => pkg.trackingId === trackingIdParam && pkg.consumerSupportCase
      )

      if (packageWithCase?.consumerSupportCase) {
        const supportCase = packageWithCase.consumerSupportCase
        setIssueData({
          trackingId: trackingIdParam,
          issueType: mapCaseCategoryToIssueType(supportCase.caseCategory),
          issueDetail: supportCase.caseDetails || '',
          issueSubmissionResult: IssueSubmissionResult.ALREADY_SUBMITTED,
          name: '', // Contact fields not available for preexisting cases
          email: '',
          cellPhone: '',
          consentChecked: false,
          issueId: supportCase.caseId,
          imageData: null,
        })
      }
      // If no existing case found, issueData remains null (form will be shown)
    }
  }, [order, orderData?.order, trackingIdParam])

  // TO DO: Re-enable photo upload as fast follow once WAF rules are configured
  // const handleSetImageData = useCallback((newImageData: ImageData | null) => {
  //   imageDataRef.current = newImageData
  // }, [])

  const handleSaveSupportCase = useCallback(
    async ({
      trackingId,
      caseId,
      caseCategory,
      caseStatus,
    }: {
      trackingId: string
      caseId: string
      caseCategory: ConsumerSupportCaseCategory
      caseStatus: ConsumerSupportCaseStatus
    }) => {
      try {
        await AsyncStorage.setItem(
          `supportCase_${trackingId}`,
          JSON.stringify({ caseCategory, caseStatus, caseId, trackingId })
        )
      } catch (error) {
        logError('ReportIssuePage - handleSaveSupportCase error', { error })
      }
    },
    []
  )

  const handleFormSubmit = useCallback(
    async (values: IssueFormValues) => {
      // Do not submit for deflection issue types — deflection UI handles this case
      if (DEFLECTION_ISSUE_TYPES.has(values.issueType as IssueType)) {
        return
      }
      // TO DO: Re-enable photo upload as fast follow once WAF rules are configured
      // const currentImageData = imageDataRef.current
      const pkgId = order?.packages.find(
        pkg => pkg.trackingId === trackingIdParam || pkg.trackingId === values.trackingId
      )?.packageId

      if (!order?.orderId || !pkgId) {
        logError('ReportIssuePage - handleFormSubmit - order or packageId not found', { order, pkgId })
        showToast({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title="Unable to Submit Issue"
              description="Sorry, something went wrong submitting your issue. Please reload the page and try again or contact support."
              status="error"
              variant="subtle"
              isClosable={true}
              closeToast={closeToast}
            />
          ),
          duration: 5000,
        })
        return
      }

      const caseInput: CreateConsumerSupportCaseInput = {
        caseCategory: mapIssueTypeToCaseCategory(values.issueType),
        caseDetails: values.issueDetail,
        orderId: order?.orderId,
        clientId: order?.clientId,
        packageId: pkgId,
        requesterEmail: values.email,
        requesterName: values.name,
        requesterPhone: stripPhoneNumber(values.cellPhone) || values.cellPhone,
        // TO DO: Re-enable photo upload as fast follow once WAF rules are configured
        // attachmentData: currentImageData
        //   ? { attachmentDataUrl: currentImageData.dataUrl, attachmentType: AttachmentType.Photo }
        //   : undefined,
      }

      try {
        const result = await createConsumerSupportCase({
          variables: {
            caseInput,
          },
        })

        if (result.data?.createConsumerSupportCase) {
          const caseData = result.data.createConsumerSupportCase
          // Set issueData with data from the mutation response and submitted form values
          // Note: Order query is automatically refetched via refetchQueries in mutation options
          setIssueData({
            trackingId: values.trackingId,
            issueType: values.issueType,
            issueDetail: values.issueDetail,
            issueSubmissionResult: IssueSubmissionResult.SUCCESS,
            name: values.name,
            email: values.email,
            cellPhone: values.cellPhone,
            consentChecked: values.consentChecked,
            issueId: caseData.caseId,
            taskCreated: caseData.taskCreated,
            // TO DO: Re-enable photo upload as fast follow once WAF rules are configured
            // imageData: currentImageData,
          })
          await handleSaveSupportCase({
            trackingId: values.trackingId,
            caseId: caseData.caseId,
            caseCategory: mapIssueTypeToCaseCategory(values.issueType),
            caseStatus: caseData.caseStatus,
          })
        }
      } catch (error) {
        logError('ReportIssuePage - handleFormSubmit error', { error })

        showToast({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title="Unable to Submit Issue"
              description="Sorry, something went wrong submitting your issue. Please try again or contact support."
              status="error"
              variant="subtle"
              isClosable={true}
              closeToast={closeToast}
            />
          ),
          duration: 5000,
        })
      }
    },
    [createConsumerSupportCase, order, setIssueData, showToast, closeToast, trackingIdParam, handleSaveSupportCase]
  )

  const DeliveryIssueTrackingIdFormInput = useCallback(() => {
    const isSubmitting = isSubmittingCase || isLoadingOrder

    return (
      <Box display="flex" flexDirection="column" alignItems="center" width={isMobileScreen ? '100%' : '350px'}>
        <Formik<IssueFormValues>
          initialValues={{
            trackingId: trackingIdParam || '',
            issueType: '',
            issueDetail: '',
            name: '',
            email: '',
            cellPhone: '',
            consentChecked: false,
          }}
          validationSchema={issueFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {(formikProps: FormikProps<IssueFormValues>) => {
            const { values, errors, handleSubmit } = formikProps
            const isFormValid =
              !errors.trackingId &&
              values.trackingId.trim().length > 0 &&
              !errors.issueType &&
              values.issueType.trim().length > 0 &&
              !errors.name &&
              values.name.trim().length > 0 &&
              !errors.email &&
              values.email.trim().length > 0 &&
              !errors.cellPhone &&
              values.cellPhone.trim().length > 0 &&
              !errors.consentChecked &&
              values.consentChecked
            const showRestOfForm = !DEFLECTION_ISSUE_TYPES.has(values.issueType as IssueType)

            return (
              <Box width="100%">
                <DeliveryDetailsSection
                  formikProps={formikProps}
                  trackingIdParam={trackingIdParam}
                  isSubmitting={isSubmitting}
                />
                {showRestOfForm && (
                  <>
                    <YourDetailsSection formikProps={formikProps} />
                    <ConsentCheckbox formikProps={formikProps} />
                    <SubmitButton onPress={() => handleSubmit()} isLoading={isSubmitting} isDisabled={!isFormValid} />
                  </>
                )}
              </Box>
            )
          }}
        </Formik>
      </Box>
    )
  }, [trackingIdParam, isMobileScreen, handleFormSubmit, isSubmittingCase, isLoadingOrder])

  const isIssueDataValid = issueData?.trackingId && issueData?.issueType && issueData?.issueId
  const isLoading = isLoadingOrder || isSubmittingCase

  return (
    <Box
      mx="auto"
      display="flex"
      flexDirection="column"
      width={isMobileScreen ? '100%' : '350px'}
      px={isMobileScreen && !isIssueDataValid ? 4 : 0}
    >
      {isLoading && !isIssueDataValid ? (
        <Loader style={loaderSpacing} message={isLoadingOrder ? LOADING_ORDER_MESSAGE : SUBMITTING_ISSUE_MESSAGE} />
      ) : isIssueDataValid ? (
        <IssueSubmission issueData={issueData} />
      ) : (
        <>
          {trackingIdParam && order && (
            <OrderInfoHeader clientName={order.clientDisplayName || order.clientName} trackingId={trackingIdParam} />
          )}
          <PageHeader trackingIdParam={trackingIdParam} />
          <DeliveryIssueTrackingIdFormInput />
        </>
      )}
    </Box>
  )
}
